/**
 * 统一请求工具
 * 自动从环境变量读取 base URL，注入 CloudBase 会话凭证，统一错误处理
 */
import { toast } from 'sonner'
import { auth } from '@/lib/cloudbase'
import { useAuthStore } from '@/stores/auth-store'

const BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

export interface RequestOptions {
  /** 跳过认证 */
  skipAuth?: boolean
  /** 自定义请求头 */
  headers?: Record<string, string>
  /** 静默模式：失败时不弹 toast（由调用方自行处理） */
  silent?: boolean
}

/**
 * 从 CloudBase SDK 获取认证头
 * - Authorization: Bearer <accessToken> 用于 CloudBase 网关自动注入 userId
 * - userid: <uid> 用于后端 authLoginToken 直接读取
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {}
  const store = useAuthStore.getState()

  try {
    const { data, error } = await auth.getSession()
    if (!error && data.session) {
      const token = data.session.access_token
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
      const uid = (data.user as any)?.uid || (data.session as any).sub
      if (uid) {
        headers['userid'] = uid
      }
    }
  } catch {
    // Auth not available — fall through to store fallback
  }

  // Fallback: 登录后 CloudBase SDK 会话可能未立即就绪，
  // 从 auth store 补全 userid，确保后端快速路径可用
  if (!headers['userid'] && store.user?.uid) {
    headers['userid'] = store.user.uid
  }

  if (store.user?.tenantCode) {
    headers['userten'] = store.user.tenantCode
  }

  return headers
}

/**
 * 统一 POST 请求
 * @param path 请求路径（不含 base URL）
 * @param data 请求体数据
 * @param options 可选配置
 * @returns 业务数据（response.data）
 */
export async function request<T = any>(
  path: string,
  data: Record<string, any> = {},
  options: RequestOptions = {},
): Promise<T> {
  const authHeaders = options.skipAuth ? {} : await getAuthHeaders()

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...authHeaders,
    ...options.headers,
  }

  const resp = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers,
    credentials: 'include',
    body: JSON.stringify(data),
  })

  if (resp.status === 401) {
    toast.error('登录已过期，请重新登录')
    const returnUrl = encodeURIComponent(window.location.pathname + window.location.search)
    window.location.href = `/login?returnUrl=${returnUrl}`
    throw new Error('认证已过期')
  }

  // 检查响应是否为 JSON，非 JSON 响应（如 HTML 错误页）给出明确提示
  const contentType = resp.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) {
    const text = await resp.text()
    console.error(`[request] 非 JSON 响应: status=${resp.status}, contentType=${contentType}, body=${text.slice(0, 200)}`)
    throw new Error(`服务端返回异常 (HTTP ${resp.status})，请稍后重试`)
  }

  const json = await resp.json()

  if (json.code === 401) {
    toast.error('登录已过期，请重新登录')
    const returnUrl = encodeURIComponent(window.location.pathname + window.location.search)
    window.location.href = `/login?returnUrl=${returnUrl}`
    throw new Error('认证已过期')
  }

  if (json.code !== 200) {
    const message = json.message || `请求失败 (${json.code})`
    if (!options.silent) toast.error(message)
    throw new Error(message)
  }

  return json.data
}
